import React from 'react';
import { Breadcrumb as AntBreadcrumb } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authenticatedUserRoutes, publicRoutes, superUserRoutes } from '../../routes/allRoutes';

const Breadcrumb = () => {
  const location = useLocation();
  const { t } = useTranslation();
  
  const pathSnippets = location.pathname.split('/').filter(i => i);
  
  // Combine all routes into a single array
  const allRoutes = [...authenticatedUserRoutes, ...publicRoutes, ...superUserRoutes];
  
  // Memoize route patterns for performance
  const routePatterns = React.useMemo(() => {
    const patterns = [];
    allRoutes.forEach(route => {
      // Exact match
      patterns.push({ type: 'exact', path: route.path });
      
      // Pattern match for dynamic routes
      if (route.path.includes(':')) {
        // Escape special regex characters and replace :param with [^/]+
        const escaped = route.path
          .replace(/[.+*?^${}()|[\]\\]/g, '\\$&') // Escape special chars
          .replace(/:[^/]+/g, '[^/]+'); // Replace :param with pattern
        patterns.push({ type: 'pattern', regex: new RegExp(`^${escaped}$`) });
      }
    });
    return patterns;
  }, []);
  
  // Check if a path is a valid route
  const isValidRoute = React.useCallback((path) => {
    // Check exact matches first
    const exactMatch = routePatterns.find(p => p.type === 'exact' && p.path === path);
    if (exactMatch) {
      return true;
    }
    
    // Check pattern matches
    return routePatterns.some(p => 
      p.type === 'pattern' && p.regex.test(path)
    );
  }, [routePatterns]);
  
  const breadcrumbNameMap = {
    'dashboard': 'Dashboard',
    'user_dashboard': 'Dashboard',
    'patients': 'Patient Records',
    'terminology': 'Clinical Terminology',
    'codesystems': 'Code Systems',
    'valuesets': 'Value Sets',
    'standardcodes': 'Standard Codes',
    'settings': 'Settings',
    'user_management': 'Staff & Access',
    'applicationUsers': 'Application Users',
    'business_organisation_management': 'Organisations',
    'business_organisation_data_management': 'Data Sharing Agreements',
    'templates': 'Templates',
    'smtp_config': 'SMTP Configuration',
    'feedback': 'Feedback',
    'audit': 'Audit Logs',
    'myprofile': 'My Profile',
    'applicationUser': 'Application User',
    'reporting': 'Reporting',
    'occurrences': 'Occurrences',
    'surveillances': 'Surveillances',
    'recurrences': 'Recurrences',
    'forms': 'Dynamic Forms',
  };

  const extraBreadcrumbItems = pathSnippets.map((_, index) => {
    const url = `/${pathSnippets.slice(0, index + 1).join('/')}`;
    const name = breadcrumbNameMap[pathSnippets[index]] || pathSnippets[index];
    const isLast = index === pathSnippets.length - 1;
    const isValid = isValidRoute(url);
    
    return {
      key: url,
      title: isLast || !isValid ? (
        <span>{name}</span>
      ) : (
        <Link to={url}>{name}</Link>
      ),
    };
  });

  const breadcrumbItems = [
    {
      title: <Link to="/dashboard"><HomeOutlined /> Home</Link>,
    },
    ...extraBreadcrumbItems,
  ];

  if (pathSnippets.length === 0) {
    return null;
  }

  return (
    <AntBreadcrumb
      items={breadcrumbItems}
      style={{ marginTop: '15px' }}
    />
  );
};

export default Breadcrumb;

